/* oxlint-disable typescript/consistent-return -- LINTER migration follow up */
import { Slot, Slottable } from "@radix-ui/react-slot";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../../utils/cn";
import { LoaderIcon } from "../icons/loader-icon";
import { AnimatedCheckmark } from "./animated-checkmark";
import { ardoiseButtonVariants } from "./button.variants";

type ButtonVariantProps = VariantProps<typeof ardoiseButtonVariants>;
export type ButtonVariant = Omit<ButtonVariantProps, "iconOnly">;
export type ButtonSize = NonNullable<ButtonVariant["size"]>;
export type ButtonRenderMode = NonNullable<ButtonVariant["renderMode"]>;

interface ButtonBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
  isPending?: boolean;
  enableCheckmark?: boolean;
  "data-force-hover"?: boolean;
}

export interface ButtonProps extends ButtonBaseProps {
  variant: ButtonVariant;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const hasContent = (c: unknown) => c !== null && c !== undefined && c !== false;

function useCheckmark(
  isPending: boolean,
  enableCheckmark: boolean,
  onClick?: React.MouseEventHandler<HTMLButtonElement>,
) {
  const [showCheck, setShowCheck] = useState(false);
  const prevPending = useRef(isPending);
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (!enableCheckmark) {
      return;
    }
    // run only on true → false transition
    if (prevPending.current && !isPending && !showCheck) {
      // oxlint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setShowCheck(true);
      timer = setTimeout(() => setShowCheck(false), 1000);
    }
    prevPending.current = isPending;
    return () => timer && clearTimeout(timer);
  }, [isPending, enableCheckmark, showCheck]);

  useEffect(() => {
    return () => clearTimeout(clickTimeoutRef.current);
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (enableCheckmark) {
        setShowCheck(true);
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = setTimeout(() => {
          setShowCheck(false);
          clickTimeoutRef.current = undefined;
        }, 750);
      }
    },
    [enableCheckmark, onClick],
  );

  return { showCheck, handleClick };
}

// biome-ignore lint:complexity/noExcessiveComplexity: complexity is acceptable for this component
function ButtonImpl({
  className,
  variant,
  startIcon,
  endIcon,
  asChild = false,
  children,
  isPending = false,
  disabled,
  onClick,
  enableCheckmark = false,
  type = "button",
  ...props
}: ButtonProps): React.JSX.Element {
  const hasLabel = hasContent(children);
  const hasStart = startIcon !== null && startIcon !== undefined;
  const hasEnd = endIcon !== null && endIcon !== undefined;
  const iconOnly = !hasLabel && (hasStart || hasEnd);
  const justifyClass = hasStart || hasEnd ? "justify-between" : "justify-center";

  const variantClassName = ardoiseButtonVariants({ ...variant, iconOnly });

  const Comp = asChild ? Slot : "button";
  const gapClass = "gap-[inherit]";
  const loaderAnimate = isPending ? ("spin" as const) : undefined;

  const { showCheck, handleClick } = useCheckmark(isPending, enableCheckmark, onClick);

  return (
    <Comp
      className={cn(
        variantClassName,
        "relative ease-in-out",
        {
          "[&>*:first-child]:opacity-0": isPending || showCheck,
        },
        className,
      )}
      type={type}
      {...props}
      onClick={handleClick}
      disabled={disabled ?? isPending}
    >
      <Slottable>
        {!asChild ? (
          <span className={cn("inline-flex w-full items-center", justifyClass, gapClass)}>
            <span className={cn("inline-flex min-w-0 items-center truncate", gapClass)}>
              {startIcon}
              {children}
            </span>
            {endIcon}
          </span>
        ) : (
          children
        )}
      </Slottable>

      <span
        className={cn("pointer-events-none absolute transition-opacity ease-in-out", {
          "opacity-0": !isPending,
          "opacity-100": isPending,
        })}
      >
        <LoaderIcon animate={loaderAnimate} />
      </span>

      {!isPending && showCheck && <AnimatedCheckmark className="absolute z-10" iconSize="m" />}
    </Comp>
  );
}

export const Button = React.memo(ButtonImpl);
Button.displayName = "Button";
